import { GetComments } from "@/actions/comment/get-comments";
import { GetPublicCoverImageUrl } from "@/actions/images/get-cover-image";
import { GetPostCategories } from "@/actions/post/get-post-categories";
import { Badge } from "@/components/ui/badge";
import { sharedRouteConfig } from "@/config/shared";
import { getMinutes, shimmer, toBase64 } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { CalendarIcon, Clock10Icon, MessageCircleIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import readingTime from "reading-time";
import { v4 } from "uuid";

export const dynamic = "force-dynamic";

interface MainPostItemProps {
  post: any;
}

const MainPostItem: React.FC<MainPostItemProps> = async ({ post }) => {
  const plainString = post?.html?.replace(/<[^>]+>/g, "");
  const readTime = readingTime(plainString || "");
  const comments = await GetComments(post.id ? post.id : "");
  const categories = await GetPostCategories(post.id ? post.id : "");

  return (
    <div className="group relative w-full rounded-2xl p-1.5 transition duration-200 hover:-translate-y-1">
      <div className="relative max-w-full rounded-[0.62rem]">
        <article className="relative isolate max-w-3xl rounded-lg bg-background px-5 py-5 shadow-sm shadow-gray-300 ring-1 ring-black/5 ">
          <Link href={`${sharedRouteConfig.posts}/${post.slug}`}>
            <div>
              {post.image && (
                <div className="relative aspect-[2/1] lg:shrink-0">
                  <Image
                    src={await GetPublicCoverImageUrl(
                      post.id,
                      post.image || "",
                    )}
                    alt={post.title ?? "Cover"}
                    height={256}
                    width={256}
                    priority
                    placeholder={`data:image/svg+xml;base64,${toBase64(
                      shimmer(256, 256),
                    )}`}
                    className="absolute inset-0 h-full w-full rounded-2xl bg-gray-50 object-cover"
                  />
                  {/* <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" /> */}
                </div>
              )}
              <div>
                <div className="group relative max-w-xl">
                  <h2 className="mt-3 min-h-[48px] text-lg font-semibold leading-6 transition-colors hover:text-foreground">
                    {post.title}
                  </h2>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 text-sm sm:hidden">
                    <div className="inline-flex items-center text-muted-foreground">
                      <CalendarIcon className="h-4 w-4" />
                      <span className="ml-1">
                        {post.created_at &&
                          format(parseISO(post.created_at!), "MM/dd/yyyy")}
                      </span>
                    </div>
                    <div className="inline-flex items-center text-muted-foreground">
                      <Clock10Icon className="h-4 w-4" />
                      <span className="ml-1">
                        {getMinutes(readTime.minutes ? readTime.minutes : 0)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-3 min-h-[150px] text-sm leading-6 transition-colors hover:text-foreground">
                    {post.description}
                  </p>
                  {/* Desktop toolbar view */}
                  <div className="mt-3 hidden items-center gap-x-3 text-sm sm:flex sm:flex-wrap">
                    <div className="inline-flex items-center text-muted-foreground">
                      <CalendarIcon className="h-4 w-4" />
                      <span className="ml-1">
                        {post.created_at &&
                          format(parseISO(post.created_at!), "MMMM dd, yyyy")}
                      </span>
                    </div>
                    <div className="inline-flex items-center text-muted-foreground">
                      <Clock10Icon className="h-4 w-4" />
                      <span className="ml-1">
                        {getMinutes(readTime.minutes)}
                      </span>
                    </div>
                    <div className="inline-flex items-center text-muted-foreground">
                      <MessageCircleIcon className="h-4 w-4" />
                      <span className="ml-1">{comments?.length}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex border-t border-gray-900/5 pb-2 pt-2">
                  <div className="relative flex items-center gap-x-2">
                    {post.profiles?.avatar_url && (
                      <Image
                        src={post.profiles?.avatar_url ?? "/images/avatar.png"}
                        alt={post.profiles?.full_name ?? "Avatar"}
                        height={40}
                        width={40}
                        priority
                        placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(40, 40))}`}
                        className="h-[40px] w-[40px] rounded-full bg-gray-50 object-cover"
                      />
                    )}
                    <div className="text-sm">
                      <p className="text-gray-600">
                        {post.profiles?.full_name}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
          {/* Desktop category view */}
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-3 pt-2 text-sm">
              {categories?.slice(0, 3).map((item: any) => {
                return (
                  <Link
                    href={`${sharedRouteConfig.category}/${item.slug}`}
                    key={v4()}
                  >
                    <Badge>{item.title}</Badge>
                  </Link>
                );
              })}
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default MainPostItem;
